import type { Settings } from "../../core/types";
import { SCHEMA_VERSION } from "../migrations";

/**
 * The configuration the app boots with before a caregiver has changed anything.
 *
 * Geometry defaults are Phase 0's measured values, not the design doc's estimates:
 * `pxPerMm` 5.20 (ruler-verified) and `keyMm` 19 (11 columns of Swiss German
 * QWERTZ cap the key at 99 px; 20 mm would leave a 3.6 px gap, and gap prevents
 * mis-hits as much as size does). `specs/10-contracts.md` still says "default 24" — that
 * line is corrected as part of this phase.
 *
 * `contentBandPct` 60 is the safe default under *either* reduced acuity or a field
 * loss on an unknown side (§5.2). Calibration proposes an offset; nothing here is
 * guessed at again after that.
 */
export const DEFAULT_SETTINGS: Settings = {
    schemaVersion: SCHEMA_VERSION,

    // input — direct touch until calibration says otherwise (1.12)
    inputMode: "directTouch",
    dwellMs: 800,
    scanLoops: 3,
    scanIntervalMs: 1200,
    repeatSuppressMs: 400,
    // Deliberately far below repeatSuppressMs: see the note on the field. A tremor bounce is
    // typically under 100 ms; a deliberate double letter is over 150.
    keyRepeatSuppressMs: 120,
    slopPx: 24,
    touchOffsetCorrection: { x: 0, y: 0 },

    // geometry
    pxPerMm: 5.2,
    keyMm: 19,
    keyGapMm: 1.5,
    contentBandPct: 60,
    contentBandOffsetPct: 0,
    fontScale: 1,
    focusRingPx: 8,

    // appearance
    theme: "blackOnWhite",
    keyboardLayout: "de-CH",

    // prediction — on from Phase 2 stage 2, once learning makes the ranking honest. The row's 104 pt
    // was reserved from Phase 1, so switching it on cannot move the keyboard under a motor memory the
    // person has already built. A device already in service gets this through migration v3.
    candidateCount: 4,
    predictionEnabled: true,
    // Must match DEFAULT_WEIGHTS in core/prediction/score.ts. w6 is 0.5, not 1: at 1 a 25-character
    // compound with a corpus frequency of 20 ties with `wir` at 30 000, which is how a prediction row
    // fills with words nobody wanted (§5.5's "seductive metric"). Stage 1 corrected the scorer and the
    // stored value via migration v2 but left this default behind — so a fresh install would have got
    // the known-bad vector.
    predictionWeights: [1, 3, 1, 3, 1, 0.5],
    abbrevEnabled: true,

    // speech
    cloudTtsEnabled: true,
    piperEnabled: false,
    // Pending family review against de-CH-JanNeural; both are Switzerland North.
    voiceId: "de-CH-JanNeural",
    keyEchoEnabled: false,
    keyEchoVolume: 0.4,

    // behaviour
    confirmOnPhraseTap: false,
    lowEffortMode: false,
    lowEffortRestore: null,
    partnerRotated: false,
    // Off by default, per 3.9. A screen that jumps to full-screen text after every sentence would take the
    // keyboard away from someone who was probably about to type another one.
    autoPartnerAfterSpeak: false,
    // Off by default: it changes what Sprechen means, and somebody who speaks whole sentences would find
    // an output bar that never empties itself worse than one that does.
    keepTextAfterSpeak: false,

    pin: null,
};
